export interface IHighlightPart {
  start: number
  text: string
  isMatch: boolean
}

interface IRange {
  start: number
  end: number
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export const tokenizeSearch = (search: string) => {
  const phrase = (search || '').trim().replace(/\s+/g, ' ')
  return { phrase, tokens: phrase ? phrase.split(' ') : [] }
}

const collectRanges = (text: string, term: string): Array<IRange> => {
  const ranges: Array<IRange> = []
  const matcher = new RegExp(escapeRegExp(term), 'gi')
  let match = matcher.exec(text)

  while (match) {
    ranges.push({ start: match.index, end: match.index + match[0].length })
    matcher.lastIndex = match.index + match[0].length
    match = matcher.exec(text)
  }

  return ranges
}

const mergeRanges = (ranges: Array<IRange>): Array<IRange> => {
  const sorted = [...ranges].sort((first, second) => first.start - second.start)

  return sorted.reduce<Array<IRange>>((merged, range) => {
    const previous = merged[merged.length - 1]

    if (previous && range.start <= previous.end) {
      previous.end = Math.max(previous.end, range.end)
      return merged
    }

    return [...merged, { ...range }]
  }, [])
}

export const buildHighlightParts = (text: string, search: string): Array<IHighlightPart> => {
  const { phrase, tokens } = tokenizeSearch(search)

  if (!text) return []
  if (!phrase) return [{ start: 0, text, isMatch: false }]

  const terms = tokens.length > 1 ? [phrase, ...tokens] : tokens
  const ranges = mergeRanges(terms.reduce<Array<IRange>>((all, term) => [...all, ...collectRanges(text, term)], []))

  if (ranges.length === 0) return [{ start: 0, text, isMatch: false }]

  const parts: Array<IHighlightPart> = []
  let cursor = 0

  ranges.forEach((range) => {
    if (range.start > cursor) parts.push({ start: cursor, text: text.slice(cursor, range.start), isMatch: false })
    parts.push({ start: range.start, text: text.slice(range.start, range.end), isMatch: true })
    cursor = range.end
  })

  if (cursor < text.length) parts.push({ start: cursor, text: text.slice(cursor), isMatch: false })

  return parts
}
